import React, { useState } from "react";
import StackKit from "@/shared/components/kit/stack";
import TooltipKit from "@/shared/components/kit/tooltip";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import { Pagination, PaginationItem, useTheme, useMediaQuery, TextField } from "@mui/material";
import BoxKit from "@/shared/components/kit/box";
import CustomSelect from "@/shared/components/common/select";
import CustomButton from "@/shared/components/kit/button";

type TableProps = {
  dataHeader: { id: number | string; title: string }[];
  dataTbody: any[];

  // NEW — delete support
  onDelete?: (id: any) => void;
  deleteLoading?: boolean;

  // Pagination props from API
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
  };

  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
};

const TableWallet = (props: Partial<TableProps>) => {
  const { 
    dataHeader, 
    dataTbody = [], 
    pagination,
    onPageChange,
    onItemsPerPageChange,
    onDelete,
    deleteLoading,
  } = props;
  
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [localPage, setLocalPage] = useState(1);
  const [localItemsPerPage, setLocalItemsPerPage] = useState<number | string>(10);
  
  const hasServerPagination = !!pagination;
  
  const currentPage = hasServerPagination ? pagination!.current_page : localPage;
  const itemsPerPage = hasServerPagination 
    ? pagination!.per_page 
    : Number(localItemsPerPage) || 10;
  const totalItems = hasServerPagination 
    ? pagination!.total 
    : dataTbody.length;
  
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  const paginatedData = hasServerPagination 
    ? dataTbody
    : dataTbody.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );
  
  const handlePageChange = (newPage: number) => {
    if (hasServerPagination) onPageChange?.(newPage);
    else setLocalPage(newPage);
  };
  
  const handleItemsPerPageChange = (newSize: number) => {
    if (hasServerPagination) onItemsPerPageChange?.(newSize);
    else {
      setLocalItemsPerPage(newSize);
      setLocalPage(1);
    }
  };
  
  return (
    <StackKit direction="column" className="w-full" sx={{ padding: 1 }}>
      <StackKit
        direction={isSmall ? "column" : "row"}
        sx={{
          width: "100%",
          justifyContent: "space-between",
          py: 1,
          px: 2.5,
          height: isSmall ? "auto" : "100px",
          gap: 2,
          alignItems: "center",
        }}
      >
        {(hasServerPagination || dataTbody.length > 0) && (
          <BoxKit sx={{ width: isSmall ? "100%" : "auto" }}>
            <CustomSelect
              value={hasServerPagination ? itemsPerPage : localItemsPerPage}
              options={[5, 10, 15, 20]}
              onChange={(val) => handleItemsPerPageChange(Number(val))}
              label={
                <TypoGraphyKit variant="body1" sx={{ color: "#A4A4A4" }}>
                  تعداد نمایش
                </TypoGraphyKit>
              }
            />
          </BoxKit>
        )}

        <StackKit direction={isSmall ? "column" : "row"} sx={{ width: isSmall ? "100%" : "auto", gap: 2, alignItems: "center" }}>
          <BoxKit sx={{ width: "100%", maxWidth: isSmall ? "100%" : "375px", height: "38px" }}>
            <TextField
              type="search"
              variant="outlined"
              fullWidth
              sx={{
                borderRadius:'1rem !important',
                '& .MuiOutlinedInput-root': {
                  background:'#303030 !important',
                  borderRadius:'1rem !important',
                },
                input: {
                  borderRadius:'1rem !important',
                  boxSizing: "border-box",
                  ":placeholder-shown": { color: "#fff !important" },
                  background:'#303030 !important',
                },
                height: "100%",
                padding: 0,
              }}
              placeholder="وارد کردن جزییات جستجو( مبلغ- تاریخ- نوع - مبدا و ...)"
            />
          </BoxKit>
          <CustomButton sx={{ width: isSmall ? "100%" : "auto", py: 1, px: 5, color:'#fff' }}>
            جستجو
          </CustomButton>
        </StackKit>
      </StackKit>
      
      <BoxKit sx={{ width: "100%", overflowX: "auto" }}>
        <table className="w-full table-auto border-separate border-spacing-x-3 border-spacing-y-3">
          <thead>
            {paginatedData.length > 0 && (
              <tr className="text-white">
                <th className="text-center px-2 py-1 text-sm sm:text-base">ردیف</th>

                {dataHeader?.map(header => (
                  <th key={header.id} className="text-center px-2 py-1 text-sm sm:text-base">
                    {header.title}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          {paginatedData.length > 0 && (
            <tbody>
              {paginatedData.map((row: any, rowIndex: number) => {
                const rowNumber = (currentPage - 1) * itemsPerPage + rowIndex + 1;
                
                return (
                  <tr
                    key={row.id ?? rowIndex}
                    className="text-center text-white"
                    style={{ borderRadius: "8px", overflow: "hidden" }}
                  >
                    <td
                      className="rounded-lg bg-gradient-to-r from-[#313131] to-[#252525] px-2 py-1"
                      style={{
                        maxWidth: isSmall ? "120px" : "150px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        textAlign: "center",
                      }}
                    >
                      {toPersianNumber(rowNumber)}
                    </td>
                    
                    {dataHeader?.map((header) => (
                      <td
                        key={header.id}
                        className={`px-2 py-1 ${
                          header.id === "moreElement" ||
                          header.id === "detailButton" ||
                          header.id === "action"
                            ? "!bg-transparent"
                            : "rounded-lg bg-gradient-to-r from-[#313131] to-[#252525]"
                        }`}
                        style={{
                          maxWidth: isSmall ? "120px" : "150px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          textAlign: "center",
                        }}
                      >
                        {header.id === "action" ? (
                          <CustomButton
                            size="small"
                            sx={{ color: "#fff", background: "#ff4d4d" }}
                            onClick={() => onDelete?.(row.Transaction_id)}
                            disabled={deleteLoading}
                          >
                            {deleteLoading ? "..." : "حذف"}
                          </CustomButton>
                        ) : (
                          <span className="overflow-hidden text-ellipsis text-nowrap max-w-[100%] block text-center">
                            {row[header.id] ?? "-"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </BoxKit>

      {dataTbody && dataTbody.length === 0 && (
        <StackKit direction="row" alignItems="center" justifyContent="center" className="w-full p-12">
          <TypoGraphyKit variant="h6">اطلاعاتی یافت نشد</TypoGraphyKit>
        </StackKit>
      )}

      {totalPages > 1 && (
        <StackKit direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => handlePageChange(page)}
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'white',
              },
              '& .MuiPaginationItem-page': {
                backgroundColor: '#333',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
              '& .Mui-selected': {
                backgroundColor: '#7367F0',
                color: 'white',
              },
            }}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                page={typeof item.page === "number" ? toPersianNumber(item.page) : item.page}
              />
            )}
            size={isSmall ? "small" : "medium"}
          />
        </StackKit>
      )}
    </StackKit>
  );
};

export default TableWallet;
